/**
 * ItemSummaryReport — Report of raw materials and finished products (only Prod Safe Qty is editable)
 * with yield, labor factor, brand, QS inventory, last used, packing, sales, Prod Safe Qty and ratio.
 * Rendered as a tab inside the Reports page.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import productionService, { ItemSummaryView as SubView, ItemInventory } from '../../services/productionService';
import { formatYield, getYieldColor } from '../../hooks/useYieldFactor';
import { formatShortDate, formatMMDDYY, toLocalISODate } from '../../utils/dateUtils';
import toast from 'react-hot-toast';
import { Search, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Calendar, X } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import DatePickerDropdown from '../../components/DatePickerDropdown';
import HoverTooltip from '../../components/HoverTooltip';
import { useAuth } from '../../hooks/useAuth';
import { isElevatedRole } from '../../constants/queryConfig';
import {
  ShownInventory, inventoryAt, inventoryRatio, parseSafeQtyDraft, qsLocationOptions, ratioText, safeQtyDraftStart, safeQtyText,
} from './ItemSummaryReport/locationCases';
import LocationCasesCell from './ItemSummaryReport/LocationCasesCell';
import SafeQtyCell from './ItemSummaryReport/SafeQtyCell';
import ConfirmDialog from './ItemSummaryReport/ConfirmDialog';

interface ItemSummaryReportProps {
  embedded?: boolean;
}

interface SortState {
  field: string;
  dir: 'asc' | 'desc';
}

interface RawMaterialItem {
  item_number: string;
  product_item_number: string;  // the finished product this (material, yield) belongs to
  item_english_name: string;
  item_chinese_name: string;
  english_brand_name: string | null;
  chinese_brand_name: string | null;
  unit_weight: number | null;
  packing: string | null;
  yield_factor: number | null;  // product-specific; null when ics.raw_material yield is unset
  yield_factor_source: string | null;
  labor_factor: number | null;
  last_used_date: string | null;
  inventory?: ShownInventory;
  inventory_failed?: boolean;  // QS did not answer for this item; sorts last like a missing value
}

interface FinishedProductItem {
  item_number: string;
  item_english_name: string;
  item_chinese_name: string;
  english_brand_name: string | null;
  chinese_brand_name: string | null;
  location_code: string | null;
  unit_weight: number | null;
  packing: string | null;
  last_produced_date: string | null;
  sales_history?: number;
  sales_failed?: boolean;  // the request holding this item failed; sorts last like a missing value
  safe_qty?: number | null;  // Prod Safe Qty; null when the item has none
  safe_qty_editable?: boolean;  // the backend's item rule; the role check is added here
  inventory?: ShownInventory;
  inventory_failed?: boolean;
  materials: Array<{
    item_number: string;
    material_name: string;
    yield_factor: number;
    yield_factor_source: string;
    labor_factor: number | null;
  }>;
}

interface Brand {
  english_brand_name: string;
  chinese_brand_name: string | null;
}

const DEFAULT_FILTERS = { search: '', brand: '', location: '', hteRelevant: true };
type Filters = typeof DEFAULT_FILTERS;

const PAGE_SIZE = 25;

/** Every row of a server-paged list at the server's own page size, first copy of each key; an empty page also ends it. */
async function fetchAllRows<T>(
  fetchPage: (page: number) => Promise<{ items?: T[]; pagination?: { totalPages: number } }>,
  keyOf: (row: T) => string,
  isCancelled: () => boolean,
): Promise<T[]> {
  const rows = new Map<string, T>();
  for (let page = 1; ; page++) {
    const data = await fetchPage(page);
    const items = data.items || [];
    items.forEach(row => { if (!rows.has(keyOf(row))) rows.set(keyOf(row), row); });
    if (isCancelled() || items.length === 0 || page >= (data.pagination?.totalPages ?? 0)) return [...rows.values()];
  }
}

const ItemSummaryReport: React.FC<ItemSummaryReportProps> = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isManagerOrAdmin = isElevatedRole(user?.role);

  const [subView, setSubView] = useState<SubView>('finished-products');
  // Each tab keeps its own filters and sort
  const [filtersByView, setFiltersByView] = useState<Record<SubView, Filters>>({
    'raw-materials': DEFAULT_FILTERS,
    'finished-products': { ...DEFAULT_FILTERS, brand: 'HTE' },
  });
  const { search, brand, location, hteRelevant } = filtersByView[subView];
  // One date range for the page: it only sets the Sales History period (Finished Products) and filters no rows
  const [salesDates, setSalesDates] = useState({ startDate: '', endDate: '' });
  const { startDate: salesStartDate, endDate: salesEndDate } = salesDates;
  const datesDisabled = subView === 'raw-materials';
  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFiltersByView(prev => ({ ...prev, [subView]: { ...prev[subView], [key]: value } }));

  // Sorting state
  const [sortByView, setSortByView] = useState<Record<SubView, SortState>>({
    'raw-materials': { field: 'item_number', dir: 'asc' },
    'finished-products': { field: 'item_number', dir: 'asc' },
  });
  const { field: sortField, dir: sortDir } = sortByView[subView];
  const handleSort = (field: string) => {
    setSortByView(prev => {
      const current = prev[subView];
      const dir = current.field === field && current.dir === 'asc' ? 'desc' : 'asc';
      return { ...prev, [subView]: { field, dir } };
    });
    setPage(1);
  };
  const today = toLocalISODate(new Date());
  const [page, setPage] = useState(1);

  const [rawMaterials, setRawMaterials] = useState<RawMaterialItem[]>([]);
  const [finishedProducts, setFinishedProducts] = useState<FinishedProductItem[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [qsResult, setQsResult] = useState<{ key: string; inventory: Record<string, ItemInventory>; failed: Set<string> } | null>(null);
  const [salesResult, setSalesResult] = useState<{ key: string; sales: Record<string, number>; failed: Set<string> } | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [savingSafeQty, setSavingSafeQty] = useState<Set<string>>(new Set());  // items with a save running

  // Debounced search, per tab: the timer ignores tab switches, and each term lands only on its own tab
  const rawSearch = filtersByView['raw-materials'].search;
  const finishedSearch = filtersByView['finished-products'].search;
  const [debouncedByView, setDebouncedByView] = useState<Record<SubView, string>>({ 'raw-materials': '', 'finished-products': '' });
  const debouncedSearch = debouncedByView[subView];
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedByView({ 'raw-materials': rawSearch, 'finished-products': finishedSearch }), 300);
    return () => clearTimeout(timer);
  }, [rawSearch, finishedSearch]);

  // Brand options for SearchableDropdown
  const brandOptions = useMemo(() =>
    brands
      .filter((b, i, arr) => arr.findIndex(x => x.english_brand_name === b.english_brand_name) === i)
      .map(b => ({ value: b.english_brand_name, label: b.english_brand_name })),
    [brands]
  );

  // Load this tab's brands; ignore responses for a tab already left
  useEffect(() => {
    let cancelled = false;
    setBrands([]);
    productionService.getItemSummaryBrands(subView)
      .then((data: { brands: Brand[] }) => { if (!cancelled) setBrands(data.brands || []); })
      .catch(() => { console.warn('Failed to load brands for item summary filter'); });
    return () => { cancelled = true; };
  }, [subView]);

  // The filters the rows were loaded for; the page height is only recorded once these rows are on screen
  const loadKey = [subView, debouncedSearch, brand].join('\n');
  const settledLoadKey = useRef<string | null>(null);

  // Load all rows of the tab when filters change; a superseded load is ignored and leaves loading to the newer one
  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    const params = {
      search: debouncedSearch || undefined,
      brand: brand || undefined,
    };
    const load = async () => {
      if (subView === 'raw-materials') {
        const rows = await fetchAllRows<RawMaterialItem>(
          page => productionService.getItemSummaryRawMaterials({ ...params, page }),
          r => `${r.item_number}|${r.product_item_number}`,
          isCancelled,
        );
        if (!cancelled) setRawMaterials(rows);
      } else {
        const rows = await fetchAllRows<FinishedProductItem>(
          page => productionService.getItemSummaryFinished({ ...params, page }),
          r => r.item_number,
          isCancelled,
        );
        if (!cancelled) setFinishedProducts(rows);
      }
    };
    setLoading(true);
    load()
      .catch(err => console.error('Failed to fetch item summary:', err))
      .finally(() => { if (!cancelled) { settledLoadKey.current = loadKey; setLoading(false); } });
    return () => { cancelled = true; };
  }, [subView, debouncedSearch, brand]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [subView, debouncedSearch, brand]);

  // Unique items of all the tab's rows, as a key so only a real change of items reloads inventory
  const itemsKey = useMemo(() => {
    const rows: Array<{ item_number: string }> = subView === 'raw-materials' ? rawMaterials : finishedProducts;
    return [...new Set(rows.map(r => r.item_number))].join('\n');
  }, [subView, rawMaterials, finishedProducts]);

  // QS inventory of these rows only, derived like Sales History so loading shows a spinner, never "—"
  const settledQs = qsResult && qsResult.key === itemsKey ? qsResult : undefined;
  const qsLoading = itemsKey !== '' && !settledQs;
  const qsInventory = useMemo(() => settledQs?.inventory ?? {}, [settledQs]);
  const locationOptions = useMemo(() => qsLocationOptions(qsInventory), [qsInventory]);

  // Load QS inventory and totals without blocking the table; ignore responses for rows already left. Items QS did
  // not answer for (or a whole failed request) get a red x, never "—", one toast per load
  useEffect(() => {
    let cancelled = false;
    setQsResult(null);
    if (itemsKey) {
      productionService.getItemSummaryInventory(itemsKey.split('\n')).then(({ inventory, failed }) => {
        if (cancelled) return;
        setQsResult({ key: itemsKey, inventory, failed: new Set(failed) });
        if (failed.length > 0) toast.error(t('itemSummaryReport.inventoryFailed'));
      });
    }
    return () => { cancelled = true; };
  }, [itemsKey]);

  // The Sales History these rows and dates need. Loading is derived in render, not set in the effect, so the first paint
  // after the second date already shows the spinner, and values of any other items or dates are never shown or sorted on
  const salesKey = subView === 'finished-products' && itemsKey && salesStartDate && salesEndDate
    ? `${salesStartDate}|${salesEndDate}|${itemsKey}`
    : null;
  const settledSales = salesResult && salesResult.key === salesKey ? salesResult : undefined;
  const salesLoading = salesKey !== null && !settledSales;

  // Load Sales History once both dates are set, Finished Products only, all items in one request. A failed request marks
  // every item failed (red x, never 0), one toast per load
  useEffect(() => {
    let cancelled = false;
    setSalesResult(null);
    if (salesKey) {
      const items = itemsKey.split('\n');
      productionService.getItemSummarySalesHistory(items, salesStartDate, salesEndDate)
        .then(data => {
          if (!data?.sales) throw new Error('answer has no sales');
          if (!cancelled) setSalesResult({ key: salesKey, sales: data.sales, failed: new Set() });
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Failed to load sales history:', err);
          setSalesResult({ key: salesKey, sales: {}, failed: new Set(items) });
          toast.error(t('itemSummaryReport.salesHistoryFailed'));
        });
    }
    return () => { cancelled = true; };
  }, [subView, itemsKey, salesStartDate, salesEndDate]);

  // Fill each row's inventory; the selected location picks the cell, and the ratio and sorts follow what the cell shows
  const withInventory = useCallback(<T extends { item_number: string }>(items: T[]) =>
    items.map(item => ({
      ...item,
      inventory: inventoryAt(qsInventory[item.item_number], location),
      inventory_failed: settledQs?.failed.has(item.item_number),
    })),
    [qsInventory, settledQs, location]
  );
  const shownRawMaterials = useMemo(() => withInventory(rawMaterials), [withInventory, rawMaterials]);
  const shownFinishedProducts = useMemo(
    () => withInventory(finishedProducts).map(item => ({
      ...item,
      sales_history: settledSales?.sales[item.item_number],
      sales_failed: settledSales?.failed.has(item.item_number),
    })),
    [withInventory, finishedProducts, settledSales]
  );

  // Sort all rows, then the tables get one page of them
  const rawSort = sortByView['raw-materials'];
  const finishedSort = sortByView['finished-products'];
  const sortedRawMaterials = useMemo(() => sortRawMaterials(shownRawMaterials, rawSort), [shownRawMaterials, rawSort]);
  const sortedFinishedProducts = useMemo(() => sortFinishedProducts(shownFinishedProducts, finishedSort), [shownFinishedProducts, finishedSort]);
  const total = subView === 'raw-materials' ? rawMaterials.length : finishedProducts.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const pageOf = <T,>(rows: T[]) => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // The row takes the saved Prod Safe Qty from the response; the ratio cell spins until the page recomputes it.
  const saveSafeQty = async (itemNumber: string, quantity: number) => {
    setSavingSafeQty(prev => new Set(prev).add(itemNumber));
    try {
      const { safe_qty } = await productionService.saveSafetyQuantities(itemNumber, quantity);
      setFinishedProducts(prev => prev.map(i => (i.item_number === itemNumber ? { ...i, safe_qty } : i)));
    } catch (err) {
      console.error('Failed to save safe qty:', err);
      toast.error(t('itemSummaryReport.saveSafeQtyFailed'));
    } finally {
      setSavingSafeQty(prev => {
        const next = new Set(prev);
        next.delete(itemNumber);
        return next;
      });
    }
  };

  // While rows load, keep the last loaded height, so the page scrollbar can't vanish and shift the filter bar sideways
  const pageRef = useRef<HTMLDivElement>(null);
  const loadedHeight = useRef<number | undefined>(undefined);
  const holdHeight = loading || settledLoadKey.current !== loadKey;  // also the render right after a filter change, before loading is set
  useLayoutEffect(() => {
    if (!holdHeight && pageRef.current) loadedHeight.current = pageRef.current.offsetHeight;
  });

  const toggleExpand = (itemNumber: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(itemNumber)) next.delete(itemNumber);
      else next.add(itemNumber);
      return next;
    });
  };

  return (
    <div ref={pageRef} className="space-y-4" style={holdHeight ? { minHeight: loadedHeight.current } : undefined}>
      {/* Filters bar — segmented toggle + search + dropdowns + dates */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Segmented toggle: Raw Materials / Finished Products */}
          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
            <button
              onClick={() => setSubView('raw-materials')}
              className={segmentClass(subView === 'raw-materials')}
            >
              {t('itemSummaryReport.rawMaterials')}
            </button>
            <button
              onClick={() => setSubView('finished-products')}
              className={segmentClass(subView === 'finished-products')}
            >
              {t('itemSummaryReport.finishedProducts')}
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-7 bg-gray-200 dark:bg-gray-600" />

          {/* Search */}
          <div className="relative flex-1 min-w-[120px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setFilter('search', e.target.value)}
              placeholder={t('itemSummaryReport.search')}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Brand searchable dropdown */}
          <SearchableDropdown
            options={brandOptions}
            value={brand}
            onChange={value => setFilter('brand', value)}
            placeholder={t('itemSummaryReport.allBrands')}
            noMatchesText={t('common.noResults')}
            className="w-40"
          />

          {/* Location searchable dropdown */}
          <SearchableDropdown
            options={locationOptions}
            value={location}
            onChange={value => setFilter('location', value)}
            placeholder={t('itemSummaryReport.allLocations')}
            noMatchesText={t('common.noResults')}
            className="w-36"
          />

          {/* Date range: Sales History only, so greyed on Raw Materials (the pointer passes to the hint around it) */}
          {([
            ['startDate', salesStartDate, undefined, salesEndDate || today],
            ['endDate', salesEndDate, salesStartDate || undefined, today],
          ] as const).map(([key, value, min, max]) => (
            <DatePickerDropdown key={key} selected={value || null} onSelect={date => setSalesDates(prev => ({ ...prev, [key]: date }))} onClear={() => setSalesDates(prev => ({ ...prev, [key]: '' }))} min={min} max={max} align="auto" disabled={datesDisabled}>
              {({ onClick }) => (
                <HoverTooltip label={datesDisabled ? t('itemSummaryReport.datesSalesHistoryOnly') : ''} textClassName="text-xs font-normal">
                  <button type="button" onClick={onClick} disabled={datesDisabled} className="flex items-center justify-between gap-2 w-36 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white whitespace-nowrap disabled:pointer-events-none disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500" title={datesDisabled ? undefined : t(`itemSummaryReport.${key}`)}>
                    <span className="truncate">{value ? formatMMDDYY(value) : `${t(`itemSummaryReport.${key}`)}...`}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      {value && !datesDisabled && (
                        <span role="button" aria-label={t('common.clear')} title={t('common.clear')} onClick={e => { e.stopPropagation(); setSalesDates(prev => ({ ...prev, [key]: '' })); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-base leading-none">×</span>
                      )}
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </span>
                  </button>
                </HoverTooltip>
              )}
            </DatePickerDropdown>
          ))}

          {/* HTE Relevant toggle (not connected yet) */}
          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
            <button onClick={() => setFilter('hteRelevant', !hteRelevant)} aria-pressed={hteRelevant} className={segmentClass(hteRelevant)}>
              <HoverTooltip label={t('itemSummaryReport.hteRelevantHint')} textClassName="text-xs font-normal" align="end">
                {t('itemSummaryReport.hteRelevant')}
              </HoverTooltip>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3" />
            {t('common.loading')}
          </div>
        ) : subView === 'raw-materials' ? (
          <RawMaterialsTable items={pageOf(sortedRawMaterials)} t={t} sortField={sortField} sortDir={sortDir} onSort={handleSort} qsLoading={qsLoading} />
        ) : (
          <FinishedProductsTable
            items={pageOf(sortedFinishedProducts)}
            expandedRows={expandedRows}
            toggleExpand={toggleExpand}
            t={t}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            canEditSafeQty={isManagerOrAdmin}
            onSaveSafeQty={saveSafeQty}
            savingSafeQty={savingSafeQty}
            salesLoading={salesLoading}
            qsLoading={qsLoading}
          />
        )}

        {/* Empty state */}
        {!loading && total === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p className="text-lg font-medium">{t('itemSummaryReport.noData')}</p>
            <p className="text-sm mt-1">{t('itemSummaryReport.noDataHint')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            {t('itemSummaryReport.showing')} {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} {t('itemSummaryReport.of')} {total} {t('itemSummaryReport.items')}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1">
              {t('itemSummaryReport.page')} {page} {t('itemSummaryReport.of')} {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Segmented-toggle button style (active = raised/highlighted) */
const segmentClass = (active: boolean) =>
  `px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
    active
      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
  }`;

// Fixed widths so sorting or paging never moves a column; Name ('') takes the rest. min-w = widths + 120px for Name.
// Spanish labels are longer, so Spanish has its own set. Raw Brand's 180px fits every brand but one.
const RAW_COLUMNS = {
  default: { minWidth: 'min-w-[1017px]', cols: ['w-[200px]', '', 'w-[180px]', 'w-[132px]', 'w-[130px]', 'w-[135px]', 'w-[120px]'] },
  es: { minWidth: 'min-w-[1169px]', cols: ['w-[200px]', '', 'w-[180px]', 'w-[132px]', 'w-[201px]', 'w-[212px]', 'w-[124px]'] },
};
const FINISHED_COLUMNS = {
  default: { minWidth: 'min-w-[1257px]', cols: ['w-[26px]', 'w-[116px]', '', 'w-[118px]', 'w-[104px]', 'w-[139px]', 'w-[194px]', 'w-[132px]', 'w-[156px]', 'w-[112px]'] },
  es: { minWidth: 'min-w-[1330px]', cols: ['w-[26px]', 'w-[116px]', '', 'w-[118px]', 'w-[113px]', 'w-[176px]', 'w-[194px]', 'w-[132px]', 'w-[175px]', 'w-[120px]'] },
};

/** Sets the fixed width of each column of a table */
const ColumnWidths = ({ cols }: { cols: string[] }) => (
  <colgroup>{cols.map((width, i) => <col key={i} className={width} />)}</colgroup>
);

/** Sortable table header cell */
function SortableHeader({ field, label, sortField, sortDir, onSort, padX = 'px-4', tooltip }: {
  field: string; label: string; sortField: string; sortDir: 'asc' | 'desc'; onSort: (field: string) => void; padX?: string; tooltip?: string;
}) {
  const isActive = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      className={`${padX} py-3 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap cursor-pointer select-none hover:text-gray-900 dark:hover:text-white transition-colors`}
    >
      <div className="flex items-center gap-1">
        {tooltip ? <HoverTooltip label={tooltip} textClassName="text-xs font-normal">{label}</HoverTooltip> : label}
        {isActive ? (
          sortDir === 'asc'
            ? <ChevronUp className="w-3.5 h-3.5" />
            : <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-0 group-hover:opacity-30" />
        )}
      </div>
    </th>
  );
}

/** Generic sorter for table rows; sorts by a field name or a derived value */
function sortItems<T>(items: T[], field: string | ((item: T) => unknown), dir: 'asc' | 'desc'): T[] {
  const get = typeof field === 'function' ? field : (item: T) => (item as unknown as Record<string, unknown>)[field];
  return [...items].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    // Nulls last
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    // Numbers
    if (typeof av === 'number' && typeof bv === 'number') {
      return dir === 'asc' ? av - bv : bv - av;
    }
    // Strings
    const sa = String(av).toLowerCase();
    const sb = String(bv).toLowerCase();
    return dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
  });
}

/** Raw Materials rows in the header's sort order */
function sortRawMaterials(items: RawMaterialItem[], { field, dir }: SortState): RawMaterialItem[] {
  // Map sort field to data key
  const fieldMap: Record<string, string> = {
    item_number: 'item_number',
    item_name: 'item_english_name',
    brand: 'english_brand_name',
    yield_factor: 'yield_factor',
    labor_factor: 'labor_factor',
    last_used: 'last_used_date',
  };
  return sortItems(items, field === 'inventory' ? i => i.inventory?.total : fieldMap[field] || 'item_number', dir);
}

/** Finished Products rows in the header's sort order; Inventory and the ratio sort on what the cells show */
function sortFinishedProducts(items: FinishedProductItem[], { field, dir }: SortState): FinishedProductItem[] {
  const fieldMap: Record<string, string> = {
    item_number: 'item_number',
    item_name: 'item_english_name',
    brand: 'english_brand_name',
    packing: 'packing',
    materials: 'materials',
    sales_history: 'sales_history',
  };
  // Derived (non-field) sort values
  const derived: Record<string, (i: FinishedProductItem) => unknown> = {
    materials: i => i.materials.length,
    safe_qty: i => i.safe_qty,
    inventory: i => i.inventory?.total,
    inventory_ratio: i => inventoryRatio(i.inventory?.total, i.safe_qty),
  };
  return sortItems(items, derived[field] ?? (fieldMap[field] || 'item_number'), dir);
}

/** Raw Materials data table */
function RawMaterialsTable({ items, t, sortField, sortDir, onSort, qsLoading }: {
  items: RawMaterialItem[]; t: (key: string) => string;
  sortField: string; sortDir: 'asc' | 'desc'; onSort: (field: string) => void; qsLoading: boolean;
}) {
  const { language } = useLanguage();
  if (items.length === 0) return null;
  const columns = language === 'es' ? RAW_COLUMNS.es : RAW_COLUMNS.default;

  return (
    <table className={`w-full table-fixed text-sm ${columns.minWidth}`}>
      <ColumnWidths cols={columns.cols} />
      <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
        <tr>
          <SortableHeader field="item_number" label={t('itemSummaryReport.itemNumber')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="item_name" label={t('itemSummaryReport.itemName')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="brand" label={t('itemSummaryReport.brand')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="inventory" label={t('itemSummaryReport.inventory')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="yield_factor" label={t('itemSummaryReport.yieldFactor')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="labor_factor" label={t('itemSummaryReport.laborFactor')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="last_used" label={t('itemSummaryReport.lastUsed')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {items.map(item => (
          <tr key={`${item.item_number}|${item.product_item_number}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-gray-900 dark:text-gray-200">
            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
              {item.item_number}
              {item.product_item_number && (
                <sub className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">({item.product_item_number})</sub>
              )}
            </td>
            <td className="px-4 py-3 break-words">
              <div>{item.item_english_name}</div>
              {item.item_chinese_name && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
              )}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300 break-words">{item.english_brand_name || '—'}</td>
            <td className="px-4 py-3"><LocationCasesCell inventory={item.inventory} t={t} loading={qsLoading} failed={item.inventory_failed} /></td>
            <td className="px-4 py-3">
              {item.yield_factor == null ? (
                <span className="text-gray-400 dark:text-gray-500">—</span>
              ) : (
                <span className={`font-medium ${getYieldColor(item.yield_factor)}`}>
                  {formatYield(item.yield_factor, 2, item.yield_factor_source)}
                </span>
              )}
            </td>
            <td className="px-4 py-3 text-gray-400 dark:text-gray-500 italic whitespace-nowrap">
              {item.labor_factor != null ? item.labor_factor.toFixed(2) : t('itemSummaryReport.laborPlaceholder')}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
              {item.last_used_date ? formatShortDate(item.last_used_date) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Finished Products data table with expandable material rows */
function FinishedProductsTable({
  items, expandedRows, toggleExpand, t, sortField, sortDir, onSort, canEditSafeQty, onSaveSafeQty, savingSafeQty, salesLoading, qsLoading,
}: {
  items: FinishedProductItem[];
  expandedRows: Set<string>;
  toggleExpand: (itemNumber: string) => void;
  t: (key: string) => string;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  canEditSafeQty: boolean;  // the user's role; each row also needs the backend's safe_qty_editable
  onSaveSafeQty: (itemNumber: string, quantity: number) => void;
  savingSafeQty: Set<string>;
  salesLoading: boolean;
  qsLoading: boolean;
}) {
  const { language } = useLanguage();
  // Prod Safe Qty edit in progress (the row's Inventory Ratio reads the draft) + pending confirmation
  const [editing, setEditing] = useState<{ itemNumber: string; draft: string } | null>(null);
  const [pending, setPending] = useState<{ item: FinishedProductItem; to: number } | null>(null);
  // The box is gone when the popup closes, so focus returns to the row's pencil
  const pencils = useRef<Record<string, HTMLButtonElement | null>>({});

  if (items.length === 0) return null;

  const columns = language === 'es' ? FINISHED_COLUMNS.es : FINISHED_COLUMNS.default;
  const closeEdit = () => { setPending(null); setEditing(null); };
  const commitEdit = (item: FinishedProductItem) => {
    if (!editing || pending) return;
    const start = safeQtyDraftStart(item.safe_qty);
    const to = parseSafeQtyDraft(editing.draft);
    if (editing.draft === start || (start !== '' && to === Number(start))) setEditing(null);
    else if (to === null) {
      toast.error(t('itemSummaryReport.safeQtyInvalid'));
      setEditing(null);
    } else setPending({ item, to });
  };

  return (
    <>
    <table className={`w-full table-fixed text-sm ${columns.minWidth}`}>
      <ColumnWidths cols={columns.cols} />
      <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
        <tr>
          <th className="pl-1.5 pr-0 py-3"></th>
          <SortableHeader field="item_number" label={t('itemSummaryReport.itemNumber')} sortField={sortField} sortDir={sortDir} onSort={onSort} padX="pl-1.5 pr-4" />
          <SortableHeader field="item_name" label={t('itemSummaryReport.itemName')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="brand" label={t('itemSummaryReport.brand')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="packing" label={t('itemSummaryReport.packing')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="sales_history" label={t('itemSummaryReport.salesHistory')} sortField={sortField} sortDir={sortDir} onSort={onSort} tooltip={t('itemSummaryReport.salesHistoryHint')} />
          <SortableHeader field="safe_qty" label={t('itemSummaryReport.safeQty')} sortField={sortField} sortDir={sortDir} onSort={onSort} tooltip={t('itemSummaryReport.safeQtyHint')} />
          <SortableHeader field="inventory" label={t('itemSummaryReport.inventory')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="inventory_ratio" label={t('itemSummaryReport.inventoryRatio')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="materials" label={t('itemSummaryReport.materials')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {items.map(item => {
          const isExpanded = expandedRows.has(item.item_number);
          const hasMaterials = item.materials.length > 0;
          const draft = editing?.itemNumber === item.item_number ? editing.draft : null;
          // The shown total ÷ Prod Safe Qty; while editing, ÷ the draft (live preview)
          const ratio = ratioText(inventoryRatio(item.inventory?.total, draft === null ? item.safe_qty : parseSafeQtyDraft(draft)));
          return (
            <React.Fragment key={item.item_number}>
              <tr className="group hover:bg-gray-50 dark:hover:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                <td className="pl-1.5 pr-0 py-3">
                  {hasMaterials && (
                    <button
                      onClick={() => toggleExpand(item.item_number)}
                      className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                      }
                    </button>
                  )}
                </td>
                <td className="pl-1.5 pr-4 py-3 font-mono text-xs whitespace-nowrap">{item.item_number}</td>
                <td className="px-4 py-3 break-words">
                  <div>{item.item_english_name}</div>
                  {item.item_chinese_name && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300 break-words">{item.english_brand_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.packing || '—'}</td>
                <td className="px-4 py-3">
                  {salesLoading
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                    : item.sales_failed
                      ? <HoverTooltip label={t('itemSummaryReport.salesHistoryFailed')} textClassName="text-xs font-normal"><X className="w-4 h-4 text-red-600 dark:text-red-400" /></HoverTooltip>
                      : item.sales_history == null ? <span className="text-gray-400 dark:text-gray-500">—</span> : `${item.sales_history} ${t('itemSummaryReport.casesUnit')}`}
                </td>
                <td className="px-4 py-3">
                  <SafeQtyCell
                    quantity={item.safe_qty}
                    draft={draft}
                    canEdit={canEditSafeQty && item.safe_qty_editable === true}
                    saving={savingSafeQty.has(item.item_number)}
                    pencilRef={el => { pencils.current[item.item_number] = el; }}
                    onEdit={() => setEditing({ itemNumber: item.item_number, draft: safeQtyDraftStart(item.safe_qty) })}
                    onDraftChange={d => setEditing({ itemNumber: item.item_number, draft: d })}
                    onCommit={() => commitEdit(item)}
                    onCancel={closeEdit}
                    t={t}
                  />
                </td>
                <td className="px-4 py-3"><LocationCasesCell inventory={item.inventory} t={t} loading={qsLoading} failed={item.inventory_failed} /></td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {savingSafeQty.has(item.item_number)
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                    : ratio === '—' ? <span className="text-gray-400 dark:text-gray-500">—</span> : ratio}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  {hasMaterials ? t(`itemSummaryReport.materialCount_${item.materials.length > 1 ? 'other' : 'one'}`).replace('{{count}}', String(item.materials.length)) : '—'}
                </td>
              </tr>
              {/* Expanded material rows */}
              {isExpanded && item.materials.map(mat => (
                <tr key={`${item.item_number}-${mat.item_number}`} className="bg-gray-50/50 dark:bg-gray-700/20">
                  <td className="pl-1.5 pr-0 py-2"></td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500 dark:text-gray-400 pl-8 whitespace-nowrap">{mat.item_number}</td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400 break-words">{mat.material_name}</td>
                  <td colSpan={6} className="px-4 py-2"></td>
                  <td className="px-4 py-2">
                    <span className={`font-medium ${getYieldColor(mat.yield_factor)}`}>
                      {formatYield(mat.yield_factor, 2, mat.yield_factor_source)}
                    </span>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
    {pending && (
      <ConfirmDialog
        title={t('itemSummaryReport.saveSafeQtyTitle')}
        message={t('itemSummaryReport.saveSafeQtyBody')
          .replace('{{item}}', pending.item.item_number)
          .replace('{{from}}', safeQtyText(pending.item.safe_qty, t('itemSummaryReport.casesUnit')))
          .replace('{{to}}', `${pending.to} ${t('itemSummaryReport.casesUnit')}`)}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('common.save')}
        returnFocusTo={() => pencils.current[pending.item.item_number]}
        onCancel={closeEdit}
        onConfirm={() => { onSaveSafeQty(pending.item.item_number, pending.to); closeEdit(); }}
      />
    )}
    </>
  );
}

export default ItemSummaryReport;
